// ===== src/components/ui/ErrorBoundary.jsx =====
// React エラーバウンダリー - 起業家向けエラーハンドリング

import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // エラーが発生した場合、stateを更新してフォールバックUIを表示
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // エラーログを記録（本番環境では外部サービスに送信）
    console.error('NOBISHIRO Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // 開発環境でのみ詳細エラー情報を表示
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Details');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.groupEnd();
    }
  }

  handleReload = () => {
    // ページをリロード
    window.location.reload();
  };

  handleGoHome = () => {
    // ホームページに戻る
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mb-6">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                予期しないエラーが発生しました
              </h1>
              <p className="text-gray-600">
                NOBISHIROアプリで問題が発生しました。<br />
                以下の方法で解決を試してください。
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={this.handleReload}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                ページを再読み込み
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Home className="w-4 h-4" />
                ダッシュボードに戻る
              </button>
            </div>

            {/* 開発環境でのみエラー詳細を表示 */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left bg-gray-100 p-4 rounded-lg text-sm">
                <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                  開発者向け詳細情報
                </summary>
                <div className="space-y-2">
                  <div>
                    <strong>エラー:</strong>
                    <pre className="text-red-600 whitespace-pre-wrap break-words">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  <div>
                    <strong>スタックトレース:</strong>
                    <pre className="text-gray-600 whitespace-pre-wrap break-words text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}

            <div className="mt-6 text-sm text-gray-500">
              <p>
                問題が続く場合は、ブラウザのキャッシュをクリアするか、<br />
                開発者にお問い合わせください。
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;